import { createSlice, PayloadAction } from "@reduxjs/toolkit";

/**
 * Orders created from this client that the orders search index may not
 * return yet (the index refreshes roughly every 3s). The full create response
 * is kept so the orders list can show the order until the server returns it.
 *
 * Deliberately not persisted: entries are only useful for a few seconds.
 */

/** Order object from the create response (same shape as orders list items). */
export interface PendingOrderDetails {
  id?: string;
  _shopEgOrderId?: string;
  context?: { bap_id?: string };
  provider?: { id?: string };
  created_at?: string;
  updated_at?: string;
  [key: string]: unknown;
}

export interface PendingOrder {
  /** `${bap_id}:${id}`, which matches the list row key and the details URL id. */
  externalOrderId: string;
  /**
   * `_shopEgOrderId` of the created order. Together with `created_at` and
   * `updated_at` from `orderDetails`, it decides when the orders list has
   * caught up (see `getConfirmedPendingOrderIds`).
   */
  shopEgOrderId: string;
  orderId: number | string | null;
  orderDetails: PendingOrderDetails;
  /** Epoch ms when the order was created on this client. */
  addedAt: number;
}

export interface PendingOrdersState {
  /** Keyed by shopId so a shop only ever sees its own pending orders. */
  byShopId: Record<string, PendingOrder[]>;
}

const initialState: PendingOrdersState = {
  byShopId: {},
};

const pendingOrdersSlice = createSlice({
  name: "pendingOrders",
  initialState,
  reducers: {
    addPendingOrder(
      state,
      action: PayloadAction<{ shopId: string; order: PendingOrder }>,
    ) {
      const { shopId, order } = action.payload;
      const others = (state.byShopId[shopId] ?? []).filter(
        (pending) => pending.externalOrderId !== order.externalOrderId,
      );
      // Newest first, matching the list's default order.
      state.byShopId[shopId] = [order, ...others];
    },
    removePendingOrders(
      state,
      action: PayloadAction<{ shopId: string; externalOrderIds: string[] }>,
    ) {
      const { shopId, externalOrderIds } = action.payload;
      const current = state.byShopId[shopId];
      if (!current) return;

      const idsToRemove = new Set(externalOrderIds);
      const remaining = current.filter(
        (pending) => !idsToRemove.has(pending.externalOrderId),
      );
      if (remaining.length > 0) {
        state.byShopId[shopId] = remaining;
      } else {
        delete state.byShopId[shopId];
      }
    },
  },
});

// Stable reference so `useSelector` doesn't re-render on every store update.
const NO_PENDING_ORDERS: PendingOrder[] = [];

export const selectPendingOrdersForShop =
  (shopId: string | null | undefined) =>
  (state: { pendingOrders: PendingOrdersState }): PendingOrder[] =>
    (shopId && state.pendingOrders.byShopId[shopId]) || NO_PENDING_ORDERS;

export const { addPendingOrder, removePendingOrders } =
  pendingOrdersSlice.actions;

export default pendingOrdersSlice.reducer;
